// Strings for release 5 (0028, Phase 4 of the topic & follow-up check-in
// revision, spec §12.B): the school admin's custom question-bank screen
// and the head of school's approval queue for it. Finnish and Swedish are
// drafts pending native-speaker review, same as every other release.
//
// area.* / scale.* are display labels for platform-managed, server-driven
// lists (active_categories/active_scale_templates) — same split as
// CONCERN_CODES/topic.* in lib/constants.ts: the server is the real list,
// this is only how each code reads in each language.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

export const r5En = {
  'hs.navQuestions': 'Questions',
  'r5.sq.nav': 'Questions',

  // The six SWIPE research areas (spec §14.1).
  'area.feel': 'Feel',
  'area.connect': 'Connect',
  'area.grow': 'Grow',
  'area.become': 'Become',
  'area.contribute': 'Contribute',
  'area.safe_ground': 'Safe ground',

  // The four starter answer-scale templates (spec §14.9).
  'scale.frequency': 'Frequency',
  'scale.intensity': 'Intensity',
  'scale.quality': 'Quality',
  'scale.safety': 'Safety',

  // School admin: the custom question bank.
  'r5.sq.title': 'Your question bank',
  'r5.sq.hint': 'Add your own follow-up questions for the six wellbeing areas. A head of school approves each one before students see it.',
  'r5.sq.newTitle': 'Add a question',
  'r5.sq.editTitle': 'Edit question',
  'r5.sq.area': 'Area',
  'r5.sq.scale': 'Answer scale',
  'r5.sq.scalePreview': 'Students answer with one of these three:',
  'r5.sq.direction': 'Which answer is better?',
  'r5.sq.directionAscending': 'The higher one',
  'r5.sq.directionDescending': 'The lower one',
  'r5.sq.text': 'Question',
  'r5.sq.textHint': 'Up to 300 characters.',
  'r5.sq.weight': 'How much does a concerning answer matter?',
  'r5.sq.weightHint': 'Higher means this question comes up more often once something seems off.',
  'r5.sq.weight0': 'A little',
  'r5.sq.weight1': 'Some',
  'r5.sq.weight2': 'A lot',
  'r5.sq.save': 'Add question',
  'r5.sq.saveEdit': 'Save changes',
  'r5.sq.cancelEdit': 'Cancel',
  'r5.sq.saved': 'Saved.',
  'r5.sq.listTitle': 'Your questions',
  'r5.sq.empty': 'No questions yet — add your first one above.',
  'r5.sq.statusPending': 'Awaiting approval',
  'r5.sq.statusApproved': 'Live',
  'r5.sq.statusRetired': 'Retired',
  'r5.sq.edit': 'Edit',
  'r5.sq.submit': 'Submit for approval',
  'r5.sq.submitted': 'Sent for approval.',
  'r5.sq.delete': 'Delete',
  'r5.sq.deleteTitle': 'Delete this question?',
  'r5.sq.deleteBody': 'This removes the draft for good. You can’t undo this.',
  'r5.sq.deleted': 'Deleted.',
  'r5.sq.retire': 'Retire',
  'r5.sq.retireTitle': 'Retire this question?',
  'r5.sq.retireBody': 'Students stop seeing it. Past answers keep their meaning — retiring never deletes them.',
  'r5.sq.retiredToast': 'Retired.',

  // Head of school: the approval queue.
  'r5.hq.title': 'Question approvals',
  'r5.hq.hint': 'Custom follow-up questions your school admin has drafted, waiting for your decision.',
  'r5.hq.empty': 'Nothing waiting for approval.',
  'r5.hq.primaryDecides': 'Only the primary head of school can approve or reject questions.',
  'r5.hq.approveTitle': 'Approve this question?',
  'r5.hq.rejectTitle': 'Reject this question?',
  'r5.hq.reason': 'Reason (optional, sent back to the school admin)',
  'r5.hq.approved': 'Approved.',
  'r5.hq.rejected': 'Sent back as a draft.',

  // Errors — category_questions RPCs (0024/0025), surfaced on both screens.
  'err.unknown_category': 'Unknown or retired area.',
  'err.unknown_scale': 'Unknown or retired answer scale.',
  'err.bad_direction': 'Choose which answer is better.',
  'err.bad_weight': 'Choose how much a concerning answer matters.',
  'err.bad_text': 'The question must be 1 to 300 characters.',
  'err.unknown_question': 'That question could not be found.',
  'err.retired': 'A retired question can’t be edited.',
  'err.bad_state': 'That action isn’t available for this question right now.',
  'err.has_answers': 'This question has already been answered — retire it instead of deleting it.',
};

export type R5Strings = Shape<typeof r5En>;

export const r5Fi: R5Strings = {
  'hs.navQuestions': 'Kysymykset',
  'r5.sq.nav': 'Kysymykset',

  'area.feel': 'Tuntea',
  'area.connect': 'Yhteys',
  'area.grow': 'Kasvaa',
  'area.become': 'Kehittyä',
  'area.contribute': 'Vaikuttaa',
  'area.safe_ground': 'Turvallinen pohja',

  'scale.frequency': 'Useus',
  'scale.intensity': 'Voimakkuus',
  'scale.quality': 'Laatu',
  'scale.safety': 'Turvallisuus',

  'r5.sq.title': 'Koulun oma kysymyspankki',
  'r5.sq.hint': 'Lisää omia jatkokysymyksiä kuudelle hyvinvoinnin osa-alueelle. Rehtori hyväksyy jokaisen ennen kuin oppilaat näkevät sen.',
  'r5.sq.newTitle': 'Lisää kysymys',
  'r5.sq.editTitle': 'Muokkaa kysymystä',
  'r5.sq.area': 'Osa-alue',
  'r5.sq.scale': 'Vastausasteikko',
  'r5.sq.scalePreview': 'Oppilas vastaa yhdellä näistä kolmesta:',
  'r5.sq.direction': 'Kumpi vastaus on parempi?',
  'r5.sq.directionAscending': 'Ylempi vaihtoehto',
  'r5.sq.directionDescending': 'Alempi vaihtoehto',
  'r5.sq.text': 'Kysymys',
  'r5.sq.textHint': 'Enintään 300 merkkiä.',
  'r5.sq.weight': 'Kuinka paljon huolestuttava vastaus painaa?',
  'r5.sq.weightHint': 'Suurempi paino tarkoittaa, että kysymys nousee useammin esiin, kun jokin näyttää olevan huolestuttavaa.',
  'r5.sq.weight0': 'Vähän',
  'r5.sq.weight1': 'Jonkin verran',
  'r5.sq.weight2': 'Paljon',
  'r5.sq.save': 'Lisää kysymys',
  'r5.sq.saveEdit': 'Tallenna muutokset',
  'r5.sq.cancelEdit': 'Peruuta',
  'r5.sq.saved': 'Tallennettu.',
  'r5.sq.listTitle': 'Omat kysymyksesi',
  'r5.sq.empty': 'Ei vielä kysymyksiä — lisää ensimmäinen yllä.',
  'r5.sq.statusPending': 'Odottaa hyväksyntää',
  'r5.sq.statusApproved': 'Käytössä',
  'r5.sq.statusRetired': 'Poistettu käytöstä',
  'r5.sq.edit': 'Muokkaa',
  'r5.sq.submit': 'Lähetä hyväksyttäväksi',
  'r5.sq.submitted': 'Lähetetty hyväksyttäväksi.',
  'r5.sq.delete': 'Poista',
  'r5.sq.deleteTitle': 'Poistetaanko tämä kysymys?',
  'r5.sq.deleteBody': 'Tämä poistaa luonnoksen pysyvästi. Toimintoa ei voi perua.',
  'r5.sq.deleted': 'Poistettu.',
  'r5.sq.retire': 'Poista käytöstä',
  'r5.sq.retireTitle': 'Poistetaanko tämä kysymys käytöstä?',
  'r5.sq.retireBody': 'Oppilaat eivät enää näe sitä. Aiemmat vastaukset säilyttävät merkityksensä — käytöstä poistaminen ei koskaan poista niitä.',
  'r5.sq.retiredToast': 'Poistettu käytöstä.',

  'r5.hq.title': 'Kysymysten hyväksyntä',
  'r5.hq.hint': 'Koulun ylläpitäjän luonnostelemat omat jatkokysymykset, jotka odottavat päätöstäsi.',
  'r5.hq.empty': 'Ei hyväksyntää odottavia kysymyksiä.',
  'r5.hq.primaryDecides': 'Vain päärehtori voi hyväksyä tai hylätä kysymyksiä.',
  'r5.hq.approveTitle': 'Hyväksytäänkö tämä kysymys?',
  'r5.hq.rejectTitle': 'Hylätäänkö tämä kysymys?',
  'r5.hq.reason': 'Syy (valinnainen, lähetetään koulun ylläpitäjälle)',
  'r5.hq.approved': 'Hyväksytty.',
  'r5.hq.rejected': 'Palautettu luonnokseksi.',

  'err.unknown_category': 'Tuntematon tai poistettu osa-alue.',
  'err.unknown_scale': 'Tuntematon tai poistettu vastausasteikko.',
  'err.bad_direction': 'Valitse, kumpi vastaus on parempi.',
  'err.bad_weight': 'Valitse, kuinka paljon huolestuttava vastaus painaa.',
  'err.bad_text': 'Kysymyksen tulee olla 1–300 merkkiä.',
  'err.unknown_question': 'Kysymystä ei löytynyt.',
  'err.retired': 'Käytöstä poistettua kysymystä ei voi muokata.',
  'err.bad_state': 'Tämä toiminto ei ole juuri nyt mahdollinen tälle kysymykselle.',
  'err.has_answers': 'Tähän kysymykseen on jo vastattu — poista se käytöstä poistamisen sijaan.',
};

export const r5Sv: R5Strings = {
  'hs.navQuestions': 'Frågor',
  'r5.sq.nav': 'Frågor',

  'area.feel': 'Känna',
  'area.connect': 'Samhörighet',
  'area.grow': 'Växa',
  'area.become': 'Utvecklas',
  'area.contribute': 'Bidra',
  'area.safe_ground': 'Trygg grund',

  'scale.frequency': 'Frekvens',
  'scale.intensity': 'Intensitet',
  'scale.quality': 'Kvalitet',
  'scale.safety': 'Trygghet',

  'r5.sq.title': 'Skolans egen frågebank',
  'r5.sq.hint': 'Lägg till egna följdfrågor för de sex välmåendeområdena. Rektorn godkänner varje fråga innan eleverna ser den.',
  'r5.sq.newTitle': 'Lägg till en fråga',
  'r5.sq.editTitle': 'Redigera fråga',
  'r5.sq.area': 'Område',
  'r5.sq.scale': 'Svarsskala',
  'r5.sq.scalePreview': 'Eleven svarar med ett av dessa tre:',
  'r5.sq.direction': 'Vilket svar är bättre?',
  'r5.sq.directionAscending': 'Det högre alternativet',
  'r5.sq.directionDescending': 'Det lägre alternativet',
  'r5.sq.text': 'Fråga',
  'r5.sq.textHint': 'Upp till 300 tecken.',
  'r5.sq.weight': 'Hur mycket väger ett oroväckande svar?',
  'r5.sq.weightHint': 'Högre vikt betyder att frågan kommer upp oftare när något verkar oroväckande.',
  'r5.sq.weight0': 'Lite',
  'r5.sq.weight1': 'En del',
  'r5.sq.weight2': 'Mycket',
  'r5.sq.save': 'Lägg till fråga',
  'r5.sq.saveEdit': 'Spara ändringar',
  'r5.sq.cancelEdit': 'Avbryt',
  'r5.sq.saved': 'Sparat.',
  'r5.sq.listTitle': 'Dina frågor',
  'r5.sq.empty': 'Inga frågor ännu — lägg till din första ovan.',
  'r5.sq.statusPending': 'Väntar på godkännande',
  'r5.sq.statusApproved': 'Aktiv',
  'r5.sq.statusRetired': 'Avaktiverad',
  'r5.sq.edit': 'Redigera',
  'r5.sq.submit': 'Skicka för godkännande',
  'r5.sq.submitted': 'Skickad för godkännande.',
  'r5.sq.delete': 'Ta bort',
  'r5.sq.deleteTitle': 'Ta bort den här frågan?',
  'r5.sq.deleteBody': 'Det här tar bort utkastet för gott. Det går inte att ångra.',
  'r5.sq.deleted': 'Borttagen.',
  'r5.sq.retire': 'Avaktivera',
  'r5.sq.retireTitle': 'Avaktivera den här frågan?',
  'r5.sq.retireBody': 'Eleverna slutar se den. Tidigare svar behåller sin betydelse — avaktivering tar aldrig bort dem.',
  'r5.sq.retiredToast': 'Avaktiverad.',

  'r5.hq.title': 'Godkännande av frågor',
  'r5.hq.hint': 'Egna följdfrågor som skolans administratör har utkast till, och som väntar på ditt beslut.',
  'r5.hq.empty': 'Inget väntar på godkännande.',
  'r5.hq.primaryDecides': 'Endast huvudrektorn kan godkänna eller avslå frågor.',
  'r5.hq.approveTitle': 'Godkänna den här frågan?',
  'r5.hq.rejectTitle': 'Avslå den här frågan?',
  'r5.hq.reason': 'Orsak (valfritt, skickas till skolans administratör)',
  'r5.hq.approved': 'Godkänd.',
  'r5.hq.rejected': 'Skickad tillbaka som utkast.',

  'err.unknown_category': 'Okänt eller avaktiverat område.',
  'err.unknown_scale': 'Okänd eller avaktiverad svarsskala.',
  'err.bad_direction': 'Välj vilket svar som är bättre.',
  'err.bad_weight': 'Välj hur mycket ett oroväckande svar väger.',
  'err.bad_text': 'Frågan måste vara 1–300 tecken.',
  'err.unknown_question': 'Frågan kunde inte hittas.',
  'err.retired': 'En avaktiverad fråga kan inte redigeras.',
  'err.bad_state': 'Den åtgärden är inte möjlig för den här frågan just nu.',
  'err.has_answers': 'Den här frågan har redan besvarats — avaktivera den i stället för att ta bort den.',
};
